import fetch from "auth/FetchInterceptor";
import requestCacheService from "./RequestCacheService";

const planoContratualService = {};

planoContratualService.get = async function (params = {}) {
	const {
		page = 0,
		pageSize = 5,
		search = "",
		orderBy = "id",
		orderDirection = "asc",
		filters = {},
	} = params;

	const queryParams = {
		page,
		pageSize,
		search,
		orderBy,
		orderDirection,
		filters,
	};

	return fetch({
		url: "/v1/rest/plano-contratual",
		method: "get",
		params: queryParams,
		headers: { is_paginated: true },
	});
};

planoContratualService.getAll = async function () {
	return requestCacheService.cacheRequest(
		"lista_plano_contratual",
		() => {
			return fetch({
				url: "/v1/rest/plano-contratual",
				method: "get",
			});
		},
		300,
	);
};

planoContratualService.post = async function (values) {
	await requestCacheService.limparCache("lista_plano_contratual");
	return fetch({
		url: "/v1/rest/plano-contratual",
		method: "post",
		data: values,
	});
};

planoContratualService.put = async function (values, id) {
	await requestCacheService.limparCache("lista_plano_contratual");
	return fetch({
		url: "/v1/rest/plano-contratual/" + id,
		method: "put",
		data: values,
	});
};

planoContratualService.getById = async (id) => {
	return fetch({
		url: `/v1/rest/plano-contratual/${id}`,
		method: "get",
	});
};

planoContratualService.delete = async (id) => {
	return fetch({
		url: `/v1/rest/plano-contratual/${id}`,
		method: "delete",
	});
};

/**
 * Obtém os planos contratuais disponíveis para contratação
 * @returns {Promise} - Promessa com os planos contratuais
 */
planoContratualService.getPlanosDisponiveis = async () => {
	return fetch({
		url: "/v1/rpc/planos-contratuais",
		method: "get",
	});
};

/**
 * Obtém o plano atual do usuário
 * @returns {Promise} - Promessa com os dados do plano atual
 */
planoContratualService.getPlanoAtual = async () => {
	return fetch({
		url: "/v1/rpc/plano-atual",
		method: "get",
	});
};

/**
 * Obtém taxas de parcelamento do cartão
 * @returns {Promise} - Promessa com as taxas
 */
planoContratualService.getTaxasParcelamentoCartao = async () => {
	return fetch({
		url: "/v1/rpc/taxas-parcelamento-cartao",
		method: "get",
	});
};

planoContratualService.getTaxasParcelamentoCartaoPublic = async () => {
	return fetch({
		url: "/v1/rpc/taxas-parcelamento-cartao",
		method: "get",
		headers: {
			"public-request": "true",
		},
	});
};

/**
 * Contrata um novo plano
 * @param {number} idPlanoContratual - ID do plano contratual
 * @param {number|null} idCupom - ID do cupom de desconto (opcional)
 * @returns {Promise} - Promessa com o resultado da contratação
 */
planoContratualService.contratarPlano = async (
	idPlanoContratual,
	idCupom = null,
) => {
	return fetch({
		url: "/v1/rpc/contratar-plano",
		method: "post",
		data: {
			idPlanoContratual,
			idCupom,
		},
	});
};

/**
 * Checkout de contratação de plano (cartão ou PIX)
 * O backend processa o pagamento via ASAAS e retorna o resultado (sucesso/PIX QR).
 */
planoContratualService.checkoutContratarPlano = async (
	idPlanoContratual,
	payload,
	idCupom = null,
) => {
	return fetch({
		url: "/v1/rpc/checkout-contratar-plano",
		method: "post",
		data: {
			idPlanoContratual,
			idCupom,
			...payload,
		},
	});
};

/**
 * Checkout por OFERTA (funil v3 / motor v2). Garante a assinatura + a fatura de
 * ativação (9,90) e paga: cartão confirma inline (PAID), PIX devolve QR.
 * @param {number} idOferta - ID da oferta (vem do GET /plano-atual v2)
 * @param {object} payload - { paymentMethod, installmentCount, cpf, creditCard,
 *   creditCardHolderInfo, saveCard, consentimentoFlipVersao }
 */
planoContratualService.checkoutOferta = async (idOferta, payload) => {
	return fetch({
		url: "/v1/rpc/checkout-oferta",
		method: "post",
		data: {
			idOferta,
			...payload,
		},
		// O 400 OFERTA_INTRO_JA_USADA (CPF já usou o intro) é tratado inline no
		// CheckoutOferta (Modal de retry no preço base); sem o toast genérico do
		// interceptor pra não duplicar o sinal junto do Modal. A flag
		// aceitarPrecoBase do retry flui pelo ...payload.
		headers: { "no-notification": true },
	});
};

/**
 * Confirma e paga a diferença do flip AGORA (PR 9c). Executa o flip cedo e cobra
 * a pró-rata: cartão confirma inline (PAID), PIX devolve QR, ou DONE se não há
 * diferença a cobrar.
 * @param {object} payload - { paymentMethod, cpf, creditCard, creditCardHolderInfo, saveCard }
 */
planoContratualService.confirmarFlipAgora = async (payload) => {
	return fetch({
		url: "/v1/rpc/confirmar-flip-agora",
		method: "post",
		data: { ...payload },
	});
};

/**
 * Troca de plano com MUDANÇA DE CICLO (mensal→anual) — Fase 0/1. O usuário
 * escolhe o destino (idPlanoDestino no payload). Espelha o confirmarFlipAgora.
 */
planoContratualService.trocarPlanoV2 = async (payload) => {
	return fetch({
		url: "/v1/rpc/trocar-plano-v2",
		method: "post",
		data: { ...payload },
	});
};

/**
 * Troca de plano PREPAGA (upgrade do picker / escolha da fronteira da fase):
 * paga o fixo CHEIO do destino AGORA (sem variável, sem resíduo) e o plano
 * novo começa na VIRADA do ciclo (o back avança o pago_ate na execução, sem
 * fatura de renovação). Vindo de conta bloqueada por escolha, o pagamento
 * aplica na hora e reativa. Espelha o trocar-plano-v2.
 * @param {object} payload - { idPlanoDestino, paymentMethod, cpf, creditCard,
 *   creditCardHolderInfo, installmentCount, cupomId }
 */
planoContratualService.trocarPlanoPrepagoV2 = async (payload) => {
	return fetch({
		url: "/v1/rpc/trocar-plano-prepago-v2",
		method: "post",
		data: { ...payload },
	});
};

/**
 * Adiantar um ano (quem já está no anual): paga o anual de novo agora e estende
 * o pago_ate +12m. NÃO troca de plano. Espelha o trocar-plano-v2.
 * @param {object} payload - { paymentMethod, cpf, creditCard, creditCardHolderInfo,
 *   installmentCount, saveCard, cupomId }
 */
planoContratualService.adiantarAnoV2 = async (payload) => {
	return fetch({
		url: "/v1/rpc/adiantar-ano-v2",
		method: "post",
		data: { ...payload },
	});
};

/**
 * Contratar uma opção de ESCALA (cards "Planos para escalar"): cobra o
 * valor_fixo CHEIO do plano destino agora; o back ANEXA o período ao pago_ate
 * (o front não calcula essa data). NÃO é pró-rata/troca-ciclo. Espelha o
 * adiantar-ano-v2.
 * @param {object} payload - { idPlanoDestino, paymentMethod, cpf, creditCard,
 *   creditCardHolderInfo, installmentCount, cupomId }
 */
planoContratualService.contratarEscalaV2 = async (payload) => {
	return fetch({
		url: "/v1/rpc/contratar-escala-v2",
		method: "post",
		data: { ...payload },
	});
};

/**
 * ASSINAR/TROCAR de plano pelo LEGADO no motor v2 (BUG 3 Parte B): o legado (sem
 * oferta) escolhe um plano do CATÁLOGO e paga AGORA o fixo CHEIO do destino.
 * legado_gratis → ativação (pago_ate ancora em now); legado_pago → troca (anexa o
 * período). O back ramifica pelo plano atual. NUNCA usar /contratar-plano (v1 puro
 * = dual-motor). Espelha o contratarEscalaV2.
 * @param {object} payload - { idPlanoDestino, paymentMethod, cpf, creditCard,
 *   creditCardHolderInfo, installmentCount, cupomId }
 */
planoContratualService.checkoutAssinarPlanoV2 = async (payload) => {
	return fetch({
		url: "/v1/rpc/checkout-assinar-plano-v2",
		method: "post",
		data: { ...payload },
	});
};

/**
 * Agenda o flip REVERSO (volta pro plano base na renovação) — PR 9c.2.
 * Sem pagamento: pró-rata 0, sem reembolso. POST sem body.
 */
planoContratualService.agendarFlipReverso = async () => {
	return fetch({
		url: "/v1/rpc/agendar-flip-reverso",
		method: "post",
		// 409 (elegibilidade/anti-gaming) não está no branch de mensagem do
		// interceptor → toast vazio; o handler do front já mostra a msg correta.
		headers: { "no-notification": true },
	});
};

/**
 * Cancela o flip reverso agendado (o prof desistiu da volta ao plano base).
 */
planoContratualService.cancelarFlipReverso = async () => {
	return fetch({
		url: "/v1/rpc/cancelar-flip-reverso",
		method: "post",
		headers: { "no-notification": true },
	});
};

/**
 * Agenda o DOWNGRADE (anual → mensal na renovação) — espelha o flip reverso,
 * mas trocando de CICLO. Sem pagamento: sem cobrança/estorno agora. POST sem body.
 */
planoContratualService.agendarDowngradeV2 = async () => {
	return fetch({
		url: "/v1/rpc/agendar-downgrade-v2",
		method: "post",
		// 409 (elegibilidade/anti-gaming) não está no branch de mensagem do
		// interceptor → toast vazio; o handler do front já mostra a msg correta.
		headers: { "no-notification": true },
	});
};

/**
 * Cancela o downgrade agendado (o prof desistiu de voltar pro mensal).
 */
planoContratualService.cancelarDowngradeV2 = async () => {
	return fetch({
		url: "/v1/rpc/cancelar-downgrade-v2",
		method: "post",
		headers: { "no-notification": true },
	});
};

/**
 * Agenda uma TROCA de plano pra renovação (picker unificado — modelo B). Vale
 * pra QUALQUER destino agendável (upgrade/downgrade, mesmo ciclo ou cross-ciclo)
 * vindo de plano-atual.planos_agendaveis. SEM cobrança/checkout agora: o plano
 * só muda no pago_ate. Body {idPlanoDestino}. Espelha o agendarDowngradeV2, mas
 * com destino explícito (que substitui o agendar-downgrade-v2 — mantido como
 * ALIAS no back por 1 release pro app legado).
 * @param {{idPlanoDestino:number}} params
 */
planoContratualService.agendarTrocaV2 = async ({ idPlanoDestino }) => {
	return fetch({
		url: "/v1/rpc/agendar-troca-v2",
		method: "post",
		data: { idPlanoDestino },
		// 409 (elegibilidade/capacidade) não está no branch de mensagem do
		// interceptor → toast vazio; o handler do front já mostra a msg correta.
		headers: { "no-notification": true },
	});
};

/**
 * Cancela a troca agendada (volta a renovar no plano atual). POST sem body.
 * Espelha o cancelarDowngradeV2.
 */
planoContratualService.cancelarTrocaV2 = async () => {
	return fetch({
		url: "/v1/rpc/cancelar-troca-v2",
		method: "post",
		headers: { "no-notification": true },
	});
};

/**
 * Checkout de uma cobrança existente (criada pelo cron apenas no banco).
 */
planoContratualService.checkoutCobrancaPlano = async (idCobrancaPlano, payload) => {
	return fetch({
		url: "/v1/rpc/checkout-cobranca-plano",
		method: "post",
		data: {
			idCobrancaPlano,
			...payload,
		},
	});
};

/**
 * Regularização de conta bloqueada do MOTOR V2: paga a fatura vencida (por id)
 * que deixou a assinatura inadimplente/bloqueada e a volta pra ativa. Espelha o
 * checkout-oferta (cartão inline / PIX QR); o webhook/polling completam o PIX.
 */
planoContratualService.regularizarAssinatura = async (idCobrancaPlano, payload) => {
	return fetch({
		url: "/v1/rpc/regularizar-assinatura",
		method: "post",
		data: {
			idCobrancaPlano,
			...payload,
		},
	});
};

/**
 * Verifica o status de uma cobrança
 * @param {number} idCobranca - ID da cobrança
 * @returns {Promise} - Promessa com o status atualizado da cobrança
 */
planoContratualService.verificarStatusCobranca = async (idCobranca) => {
	return fetch({
		url: `/v1/rpc/verificar-status-cobranca/${idCobranca}`,
		method: "get",
	});
};

/**
 * Verifica o status de uma cobrança pelo ID do ASAAS (rota pública)
 * @param {string} idAsaas - ID da cobrança no ASAAS
 * @param {string} cpf - CPF para validação
 * @returns {Promise} - Promessa com o status atualizado da cobrança
 */
planoContratualService.verificarStatusCobrancaAsaas = async (
	idAsaas,
	cpf,
) => {
	return fetch({
		url: `/v1/rpc/verificar-status-cobranca-asaas/${idAsaas}`,
		method: "get",
		params: { cpf },
		headers: {
			"public-request": "true",
		},
	});
};

/**
 * Obtém as cobranças do usuário
 * @returns {Promise} - Promessa com as cobranças do usuário
 */
planoContratualService.getMinhasCobrancas = async () => {
	return fetch({
		url: "/v1/rpc/minhas-cobrancas",
		method: "get",
	});
};

/**
 * Verifica se o plano atual do usuário permite adicionar mais alunos
 * @returns {Promise} - Promessa com o resultado da verificação
 */
planoContratualService.verificarLimiteAlunos = async () => {
	return fetch({
		url: "/v1/rpc/verificar-limite-alunos",
		method: "get",
	});
};

/**
 * Cancela o plano atual e volta para o plano gratuito
 * @returns {Promise} - Promessa com o resultado do cancelamento
 */
planoContratualService.cancelarPlano = async () => {
	return fetch({
		url: "/v1/rpc/cancelar-plano",
		method: "post",
	});
};

/**
 * Desfaz o cancelamento AGENDADO (MOTOR V2): limpa o cancelamento_agendado_em
 * enquanto a assinatura ainda está ATIVA, voltando a renovar normalmente. POST
 * sem body. Espelha o cancelarTrocaV2 (no-notification → o handler do front
 * mostra a própria mensagem de sucesso/erro, sem toast duplicado).
 * @returns {Promise} - Promessa com o resultado
 */
planoContratualService.desfazerCancelamentoV2 = async () => {
	return fetch({
		url: "/v1/rpc/desfazer-cancelamento-v2",
		method: "post",
		headers: { "no-notification": true },
	});
};

/**
 * Remove o método de pagamento salvo (token e dados do cartão)
 * @returns {Promise} - Promessa com o resultado da remoção
 */
planoContratualService.removerMetodoPagamentoPlano = async () => {
	return fetch({
		url: "/v1/rpc/metodo-pagamento-plano",
		method: "delete",
	});
};

/**
 * Obtém os dados da conta bloqueada (cobranças pendentes)
 * Esta rota é acessível mesmo quando o usuário está bloqueado
 * @returns {Promise} - Promessa com os dados da conta bloqueada
 */
planoContratualService.getDadosContaBloqueada = async () => {
	return fetch({
		url: "/v1/rpc/dados-conta-bloqueada",
		method: "get",
	});
};

export default planoContratualService;
